/**
 * 日報/サマリ(旧「納品件数の入力」。⑨・⑩・⑫・⑬指示 → 2026-09-10 に改称)。現行「サマリ」シートの手入力を階層で見る。
 *  階層: 日付(1) → 会社(2) → 出勤形態×報酬区分(3) → アポインター(4。今後の自動反映まで非表示)
 *        親の直下に子、子の直下に孫を字下げで並べる(会社別ページと同じ見た目)。既定は閉じる。行クリックで1段だけ開く
 *  横軸: 取得件数(対応履歴の自動値。手で直した値は赤字) ／ 納品件数 ／ 支店別(品川／在宅) ／ 人件費 ／ 交通費 ／ 会社単価 ／ 売上見込 ／ 原価率 ／ 入力
 *  **このページの「売上見込」は 納品件数 × 会社単価**(2026-09-10 指示。他ページの売上見込=取得×単価とは分母が違う)。原価率 = (人件費＋交通費) ÷ 売上見込
 *  この表は閲覧だけ。入力は各日付の右の「入力」で開く日別のポップアップ(DeliveryDayModal)で行い、その日だけ保存する(2026-09-10 指示)。
 *  PL・会社別に反映。売上実績(納品)ページはこのページに統合して廃止。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HelpTip from '@core/components/HelpTip';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { COST_STYLE_LABELS, DELIVERY_STYLES, DELIVERY_STYLE_LABELS, priceOn, type Company, type DailySummary, type DateStr, type Delivery, type DeliveryStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import ExpenseView from './ExpenseView';
import { deliveryStyleOf, fmtPct } from './calc';
import { costRateClass } from './colors';
import { fetchSummariesRange } from './dashboardRepo';
import DeliveryDayModal from './DeliveryDayModal';
import { costStyleOf, deliveryKeyOf, emptyCompanyEdit, emptyCompanyInfo, initialEdit, stylesFor, sumCompany, val, type CompanyEdit, type CompanyInfo } from './deliveryEdit';
import { fetchDeliveries } from './deliveryRepo';

/** 階層4(アポインター)は納品リスト突合の自動化まで非表示(⑫指示) */
const SHOW_APPOINTERS = false;

function monthDates(month: string): DateStr[] {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y!, m!, 0).getDate();
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}
function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y!, m! - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 支店別(品川／在宅)の納品件数。在宅 = 在宅歩合＋在宅時給 */
const branchOf = (by: Record<DeliveryStyle, number>) => ({ shinagawa: by.shinagawa, zaitaku: by.buai + by.jikyu });
/** 出勤形態別の内訳(サマリーカード用) */
function breakdownText(by: Record<DeliveryStyle, number>): string {
  return DELIVERY_STYLES.filter((k) => by[k] > 0)
    .map((k) => `${DELIVERY_STYLE_LABELS[k]} ${by[k]}`)
    .join(' / ');
}

export default function DeliveryInputPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? todayStr().slice(0, 7);
  const setMonth = useCallback((m: string) => setParams({ month: m }, { replace: true }), [setParams]);
  const dates = useMemo(() => monthDates(month), [month]);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [summaries, setSummaries] = useState<Map<DateStr, DailySummary>>(new Map());
  const [deliveries, setDeliveries] = useState<Map<DateStr, Delivery>>(new Map());
  const [loading, setLoading] = useState(true);
  /** 表示する値(保存済みの内訳。未保存の項目は自動計算): 日付 → 会社 → 内訳 */
  const [edit, setEdit] = useState<Record<DateStr, Record<string, CompanyEdit>>>({});
  /** 開いている日付 ／ 開いている会社("date|cid")。既定はどちらも閉じる(1段ずつ開く) */
  const [openDays, setOpenDays] = useState<Set<DateStr>>(new Set());
  const [openCompanies, setOpenCompanies] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  /** 入力ポップアップを開いている日付 */
  const [dayModal, setDayModal] = useState<DateStr | null>(null);
  /** ポップアップに未保存の変更があるか(別画面・別の月へ移るときの確認に使う) */
  const [modalDirty, setModalDirty] = useState(false);
  /** 経費画面の未保存 */
  const [expenseDirty, setExpenseDirty] = useState(false);
  /** 納品件数 ／ 経費 の切替(2026-09-10 指示) */
  const [tab, setTab] = useState<'delivery' | 'expense'>('delivery');
  const guard = useUnsavedGuard(modalDirty || expenseDirty);

  useEffect(() => {
    void fetchCompanies()
      .then(setCompanies)
      .catch(() => toast('会社マスタの読み込みに失敗しました。再読み込みしてください'));
  }, [toast]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const from = dates[0]!;
      const to = dates[dates.length - 1]!;
      const [s, d] = await Promise.all([fetchSummariesRange(from, to), fetchDeliveries(from, to)]);
      setSummaries(new Map(s.map((x) => [x.date, x])));
      setDeliveries(new Map(d.map((x) => [x.date, x])));
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [dates, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const active = useMemo(() => companies.filter((c) => c.status === 'active').sort((a, b) => a.id.localeCompare(b.id)), [companies]);

  /** 日付 → 会社 → 自動計算(日次集計)。取得は管理者も含める(品川)。人件費・交通費は管理者を除く(D-28) */
  const info = useMemo(() => {
    const out: Record<DateStr, Record<string, CompanyInfo>> = {};
    for (const [date, s] of summaries) {
      const map: Record<string, CompanyInfo> = {};
      for (const r of s.staff) {
        if (!r.company_id) continue;
        const ci = (map[r.company_id] ??= emptyCompanyInfo());
        if (r.work_style === 'kanri') {
          ci.appts[deliveryStyleOf(r)] += r.appointments;
          continue;
        }
        const cost = costStyleOf(r);
        let op = ci.ops.find((o) => o.op_id === r.op_id && o.cost === cost);
        if (!op) {
          op = { op_id: r.op_id, name: r.name, cost, appts: 0, labor: 0, commute: 0 };
          ci.ops.push(op);
        }
        op.appts += r.appointments;
        op.labor += r.pay - r.commute;
        op.commute += r.commute;
        ci.appts[deliveryStyleOf(r)] += r.appointments;
        ci.labor[cost] += r.pay - r.commute;
        ci.commute[cost] += r.commute;
      }
      out[date] = map;
    }
    return out;
  }, [summaries]);

  // 表示する値: 保存済みの内訳。取得件数・人件費・交通費は未保存なら自動計算(⑩・⑬指示)
  useEffect(() => {
    const next: Record<DateStr, Record<string, CompanyEdit>> = {};
    for (const date of dates) {
      const del = deliveries.get(date);
      const byCompany: Record<string, CompanyEdit> = {};
      for (const c of active) {
        const det = del?.details?.[c.id];
        byCompany[c.id] = initialEdit(det, det ? undefined : del?.counts?.[c.id], info[date]?.[c.id]);
      }
      next[date] = byCompany;
    }
    setEdit(next);
  }, [dates, active, deliveries, info]);

  /** その日に稼働(日次集計か保存済み内訳)のある会社。「全社を表示」で切替 */
  const companiesOf = (date: DateStr) => {
    const del = deliveries.get(date);
    return active.filter((c) => showAll || info[date]?.[c.id] || del?.details?.[c.id] || del?.counts?.[c.id] !== undefined);
  };
  const stylesOf = (date: DateStr, cid: string, e: CompanyEdit) => stylesFor(info[date]?.[cid], e, showAll);

  const sumDay = (date: DateStr) => {
    const t = { appointments: 0, delivered: 0, byStyle: { shinagawa: 0, buai: 0, jikyu: 0 } as Record<DeliveryStyle, number>, sales: 0, labor: 0, commute: 0 };
    for (const c of active) {
      const e = edit[date]?.[c.id];
      if (!e) continue;
      const s = sumCompany(e);
      t.appointments += s.appointments;
      t.delivered += s.delivered;
      for (const k of DELIVERY_STYLES) t.byStyle[k] += s.byStyle[k];
      t.sales += s.delivered * priceOn(c, date);
      t.labor += s.labor;
      t.commute += s.commute;
    }
    return t;
  };
  const monthTotal = useMemo(() => {
    const t = { appointments: 0, delivered: 0, byStyle: { shinagawa: 0, buai: 0, jikyu: 0 } as Record<DeliveryStyle, number>, sales: 0, labor: 0, commute: 0, days: 0 };
    for (const date of dates) {
      const s = sumDay(date);
      if (s.delivered > 0 || s.labor > 0 || s.commute > 0 || s.appointments > 0) t.days++;
      t.appointments += s.appointments;
      t.delivered += s.delivered;
      for (const k of DELIVERY_STYLES) t.byStyle[k] += s.byStyle[k];
      t.sales += s.sales;
      t.labor += s.labor;
      t.commute += s.commute;
    }
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, edit, active]);

  const toggleIn = (set: (f: (prev: Set<string>) => Set<string>) => void, key: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** 閲覧の値。自動計算と違えば赤字にして、カーソルで自動値を見せる */
  const viewVal = (text: string, edited: boolean, autoText: string) => (
    <span className={edited ? 'edited-val' : undefined} title={edited ? `自動 ${autoText}` : undefined}>
      {text}
    </span>
  );
  const chev = (open: boolean) => (
    <span className={`chev${open ? ' open' : ''}`}>
      <IconChevronRight size={12} />
    </span>
  );
  const leaf = (
    <span className="hint" style={{ display: 'inline-block', width: 14 }}>
      └
    </span>
  );
  const today = todayStr();
  const monthLabel = `${month.slice(0, 4)}/${month.slice(5, 7)}`;
  const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
  const COLS = (SHOW_APPOINTERS ? 11 : 10) + 1;
  /** 会社単価 ／ 売上見込(このページは 納品件数 × 会社単価。2026-09-10 指示) ／ 原価率(=(人件費＋交通費)÷売上見込) の3セル */
  const moneyCells = (unit: number | null, sales: number, labor: number, commute: number) => {
    const cost = labor + commute;
    const rate = sales > 0 ? (cost / sales) * 100 : null;
    return (
      <>
        <td className="num">{unit === null ? '' : formatYen(unit)}</td>
        <td className="num">{sales ? `¥${formatYen(sales)}` : ''}</td>
        <td className={`num ${costRateClass(rate)}`}>{fmtPct(rate)}</td>
      </>
    );
  };
  /** 支店別(品川／在宅)の2セル。日付行・会社行・合計行に出す */
  const branchCells = (by: Record<DeliveryStyle, number>, cls = 'num') => {
    const b = branchOf(by);
    return (
      <>
        <td className={cls}>{b.shinagawa || ''}</td>
        <td className={cls}>{b.zaitaku || ''}</td>
      </>
    );
  };

  return (
    <div className="screen master-screen full">
      {guard.modal}
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="tool-filter" style={{ width: '100%' }}>
          <span className="chips" style={{ marginBottom: 0, marginRight: 6 }}>
            <button className={`chip${tab === 'delivery' ? ' on' : ''}`} onClick={() => setTab('delivery')}>
              納品件数
            </button>
            <button className={`chip${tab === 'expense' ? ' on' : ''}`} onClick={() => setTab('expense')}>
              経費
            </button>
          </span>
          <button className="chip" onClick={() => setMonth(addMonths(month, -1))} aria-label="前月">
            <IconChevronLeft size={13} />
          </button>
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} style={{ height: 30, padding: '0 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12.5 }} />
          <button className="chip" onClick={() => setMonth(addMonths(month, 1))} aria-label="翌月">
            <IconChevronRight size={13} />
          </button>
          <b style={{ fontSize: 14 }}>{monthLabel}</b>
          {tab === 'delivery' && (
            <>
              <span className="hint">
                日次集計 {summaries.size}日 ／ 入力済み {deliveries.size}日
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className={`chip${showAll ? ' on' : ''}`} onClick={() => setShowAll((v) => !v)}>
                  {showAll ? '全社・全区分を表示中' : '稼働している会社のみ'}
                </button>
                <button className="txn-link" onClick={() => setOpenDays(new Set(dates))}>
                  日付をすべて開く
                </button>
                <button
                  className="txn-link"
                  onClick={() => {
                    setOpenDays(new Set());
                    setOpenCompanies(new Set());
                  }}
                >
                  すべて閉じる
                </button>
              </span>
            </>
          )}
        </div>
      </div>

      {tab === 'expense' ? (
        <div className="card">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            経費({monthLabel})
            <span className="hint" style={{ fontWeight: 400 }}>日ごとに項目別の経費を入れる。項目は空欄に名前を入れると増える</span>
          </h3>
          <ExpenseView dates={dates} monthLabel={monthLabel} onDirtyChange={setExpenseDirty} />
        </div>
      ) : loading ? (
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      ) : (
        <>
          <div className="cash-cards">
            <div className="cash total">
              <div className="nm">納品件数 ／ 売上(納品×単価)</div>
              <div className="amt">
                {monthTotal.delivered}件 ／ ¥{formatYen(monthTotal.sales)}
              </div>
              <div className="sub">
                {monthLabel}・入力のある {monthTotal.days}日・{breakdownText(monthTotal.byStyle) || '内訳なし'}
              </div>
            </div>
            <div className="cash">
              <div className="nm">人件費</div>
              <div className="amt">¥{formatYen(monthTotal.labor)}</div>
              <div className="sub">出勤人件費・在宅人件費・タイミー(入力の合計)</div>
            </div>
            <div className="cash">
              <div className="nm">交通費</div>
              <div className="amt">¥{formatYen(monthTotal.commute)}</div>
              <div className="sub">出勤者の往復交通費(入力の合計)</div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              日報/サマリ({monthLabel})
              <span className="hint" style={{ fontWeight: 400 }}>
                行をクリックして 日付 → 会社 → 出勤形態×報酬区分 の順に開く。入力は日付の右の「入力」から(人件費・交通費の初期値はその日の自動計算)。売上見込 = 納品件数 × 会社単価
              </span>
            </h3>
            <div className="table-scroll">
              <table className="fit-table tree-table fixed-cols">
                <colgroup>
                  <col />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                  {SHOW_APPOINTERS && <col style={{ width: 140 }} />}
                  <col style={{ width: 120 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 70 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>日付 ／ 会社 ／ 出勤形態×報酬区分</th>
                    <th className="num">
                      <HelpTip help={{ title: '取得件数', what: 'その日の対応履歴(アポ〇)から自動で入る件数。手で直すと赤字になり、PLのアポ数・会社別の取得件数はその値を使う', better: '—' }}>取得件数</HelpTip>
                    </th>
                    <th className="num">納品件数</th>
                    <th className="num">支店別 出勤</th>
                    <th className="num">支店別 在宅</th>
                    {SHOW_APPOINTERS && <th>アポインター</th>}
                    <th className="num">
                      <HelpTip help={{ title: '人件費(円)', what: 'PLの売上原価に使う金額。初期値は日次集計からの自動計算(出勤・タイミー＝時給×実稼働、在宅歩合＝取得×件数単価、在宅時給＝時給×BB稼働)。管理者は経費「報酬」側のため含めない', better: '—' }}>人件費</HelpTip>
                    </th>
                    <th className="num">
                      <HelpTip help={{ title: '交通費(円)', what: '出勤者の往復交通費。初期値はオペレーターマスタのその日の交通費', better: '—' }}>交通費</HelpTip>
                    </th>
                    <th className="num">会社単価</th>
                    <th className="num">
                      <HelpTip help={{ title: '売上見込', what: 'このページでは 納品件数 × 会社単価(2026-09-10 指示)。他ページの「売上見込(取得件数×単価)」とは分母が違う', better: '高いほど良い' }}>売上見込</HelpTip>
                    </th>
                    <th className="num">
                      <HelpTip help={{ title: '原価率', what: '(人件費＋交通費) ÷ 売上見込(納品件数×会社単価)', better: '低いほど良い' }}>原価率</HelpTip>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dates.flatMap((date) => {
                    const open = openDays.has(date);
                    const day = sumDay(date);
                    const has = deliveries.has(date) || summaries.has(date);
                    const cs = companiesOf(date);
                    const rows = [
                      <tr key={date} id={`day-${date}`} className={`tree-l0 clickable${date === today ? ' is-today' : ''}`} onClick={() => toggleIn(setOpenDays, date)} aria-expanded={open}>
                        <td>
                          {chev(open)}
                          {fmtDate(date)}
                          {date === today && <span className="badge-today">今日</span>}
                          {cs.length > 0 && <span className="hint"> {cs.length}社</span>}
                          {summaries.has(date) && !deliveries.has(date) && (
                            <span className="badge b-ng" style={{ marginLeft: 6 }}>
                              未入力
                            </span>
                          )}
                          {!has && <span className="hint" style={{ marginLeft: 6 }}>データなし</span>}
                        </td>
                        <td className="num">{day.appointments || ''}</td>
                        <td className="num">
                          <b>{day.delivered || ''}</b>
                        </td>
                        {branchCells(day.byStyle)}
                        {SHOW_APPOINTERS && <td></td>}
                        <td className="num">{day.labor ? `¥${formatYen(day.labor)}` : ''}</td>
                        <td className="num">{day.commute ? `¥${formatYen(day.commute)}` : ''}</td>
                        {moneyCells(null, day.sales, day.labor, day.commute)}
                        <td className="c no-clip">
                          <button
                            type="button"
                            className="btn-line"
                            style={{ padding: '2px 12px', fontSize: 12 }}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setDayModal(date);
                            }}
                          >
                            入力
                          </button>
                        </td>
                      </tr>,
                    ];
                    if (!open) return rows;
                    if (cs.length === 0) {
                      rows.push(
                        <tr key={`${date}_none`} className="tree-l1">
                          <td colSpan={COLS} className="hint" style={{ paddingLeft: 30 }}>
                            この日に稼働している会社がありません(「入力」で全社・全区分に入力できます)
                          </td>
                        </tr>,
                      );
                      return rows;
                    }
                    for (const c of cs) {
                      const key = `${date}|${c.id}`;
                      const copen = openCompanies.has(key);
                      const e = edit[date]?.[c.id] ?? emptyCompanyEdit();
                      const ci = info[date]?.[c.id];
                      const cs2 = sumCompany(e);
                      const unit = priceOn(c, date);
                      rows.push(
                        <tr key={key} className="tree-l1 clickable" onClick={() => toggleIn(setOpenCompanies, key)} aria-expanded={copen}>
                          <td style={{ paddingLeft: 30 }}>
                            {chev(copen)}
                            {c.name}
                          </td>
                          <td className="num">{cs2.appointments || ''}</td>
                          <td className="num">
                            <b>{cs2.delivered || ''}</b>
                          </td>
                          {branchCells(cs2.byStyle)}
                          {SHOW_APPOINTERS && <td></td>}
                          <td className="num">{cs2.labor ? `¥${formatYen(cs2.labor)}` : ''}</td>
                          <td className="num">{cs2.commute ? `¥${formatYen(cs2.commute)}` : ''}</td>
                          {moneyCells(unit, cs2.delivered * unit, cs2.labor, cs2.commute)}
                          <td></td>
                        </tr>,
                      );
                      if (!copen) continue;
                      for (const k of stylesOf(date, c.id, e)) {
                        const dk = deliveryKeyOf(k);
                        const autoL = ci?.labor[k] ?? 0;
                        const autoC = ci?.commute[k] ?? 0;
                        const diffL = e.labor[k].trim() === '' ? null : val(e.labor[k]) - autoL;
                        const diffC = e.commute[k].trim() === '' ? null : val(e.commute[k]) - autoC;
                        const ops = (ci?.ops ?? []).filter((o) => o.cost === k);
                        const autoA = dk ? (ci?.appts[dk] ?? 0) : 0;
                        const curA = dk ? e.appointments[dk].trim() : '';
                        const editedA = dk ? (curA === '' ? autoA > 0 : val(curA) !== autoA) : false;
                        rows.push(
                          <tr key={`${key}|${k}`} className="tree-l2">
                            <td style={{ paddingLeft: 52 }}>
                              {leaf}
                              {COST_STYLE_LABELS[k]}
                            </td>
                            <td className="num">{dk ? viewVal(curA === '' ? (editedA ? '0' : autoA ? String(autoA) : '') : curA, editedA, String(autoA)) : <span className="hint">出勤に含む</span>}</td>
                            <td className="num">{dk ? <b>{e.delivered[dk] || ''}</b> : <span className="hint">出勤に含む</span>}</td>
                            <td></td>
                            <td></td>
                            {SHOW_APPOINTERS && <td className="hint">(今後自動反映)</td>}
                            <td className="num">{viewVal(e.labor[k].trim() === '' ? '' : `¥${formatYen(val(e.labor[k]))}`, diffL !== null && diffL !== 0 && autoL > 0, `¥${formatYen(autoL)}`)}</td>
                            <td className="num">{viewVal(e.commute[k].trim() === '' ? '' : `¥${formatYen(val(e.commute[k]))}`, diffC !== null && diffC !== 0 && autoC > 0, `¥${formatYen(autoC)}`)}</td>
                            {moneyCells(unit, (dk ? val(e.delivered[dk]) : 0) * unit, val(e.labor[k]), val(e.commute[k]))}
                            <td></td>
                          </tr>,
                        );
                        if (SHOW_APPOINTERS) {
                          for (const o of ops) {
                            rows.push(
                              <tr key={`${key}|${k}|${o.op_id}`} className="tree-l2">
                                <td style={{ paddingLeft: 74 }} className="hint">
                                  {leaf}
                                  {o.name}
                                </td>
                                <td className="num hint">{o.appts}</td>
                                <td className="num hint">(自動化予定)</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="num hint">¥{formatYen(o.labor)}</td>
                                <td className="num hint">¥{formatYen(o.commute)}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>,
                            );
                          }
                        }
                      }
                    }
                    return rows;
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#EEF2F8' }}>
                    <td>合計({monthLabel})</td>
                    <td className="num">{monthTotal.appointments}</td>
                    <td className="num">{monthTotal.delivered}</td>
                    {branchCells(monthTotal.byStyle)}
                    {SHOW_APPOINTERS && <td></td>}
                    <td className="num">¥{formatYen(monthTotal.labor)}</td>
                    <td className="num">¥{formatYen(monthTotal.commute)}</td>
                    {moneyCells(null, monthTotal.sales, monthTotal.labor, monthTotal.commute)}
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
      {dayModal && (
        <DeliveryDayModal
          date={dayModal}
          companies={active}
          info={info[dayModal]}
          initial={edit[dayModal] ?? {}}
          before={deliveries.get(dayModal) ?? null}
          onClose={() => setDayModal(null)}
          onSaved={() => {
            void reload();
          }}
          onDirtyChange={setModalDirty}
        />
      )}
    </div>
  );
}
